import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Button from './Button';

interface SuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  actionText?: string;
  onAction?: () => void;
  secondaryActionText?: string;
  onSecondaryAction?: () => void;
}

const SuccessModal: React.FC<SuccessModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  actionText = 'Continue',
  onAction,
  secondaryActionText,
  onSecondaryAction,
}) => {
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10001] flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="bg-white dark:bg-gray-900 rounded-3xl max-w-sm w-full shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8 text-center">
              {/* Success Icon */}
              <div className="mb-6 relative">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', damping: 12 }}
                  className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto"
                >
                  <CheckIcon className="h-10 w-10 text-green-600 dark:text-green-400" />
                </motion.div>
                
                {/* Decorative particles (simple ones) */}
                <motion.div 
                  className="absolute inset-0 pointer-events-none"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1, 0] }}
                  transition={{ duration: 1, times: [0, 0.5, 1], repeat: 1 }}
                >
                  {/* We could add more decoration here if needed */}
                </motion.div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                {title}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-8 leading-relaxed">
                {message}
              </p>

              <div className="space-y-3">
                {onAction && (
                  <Button
                    variant="primary"
                    className="w-full py-4 rounded-xl text-lg font-semibold bg-teal-600 hover:bg-teal-700 text-white shadow-lg shadow-teal-500/20"
                    onClick={() => {
                      onAction();
                      onClose();
                    }}
                  >
                    {actionText}
                  </Button>
                )}
                
                {onSecondaryAction ? (
                  <Button
                    variant="outline"
                    className="w-full py-4 rounded-xl text-lg font-semibold border-gray-200 dark:border-gray-700"
                    onClick={() => {
                      onSecondaryAction();
                      onClose();
                    }}
                  >
                    {secondaryActionText}
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    className="w-full py-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    onClick={onClose}
                  >
                    Close
                  </Button>
                )}
              </div>
            </div>

            {/* Close button in corner */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default SuccessModal;
